import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import TabWrapper from './TabWrapper';
import { Box, Typography, Button, TextField, Grid, Alert, CircularProgress } from '@mui/material';
import { ethers } from 'ethers';
import { DevPaymentAbi } from '../abis';
import { sendEmailToDevPartner } from '../../../../../redux/actions/dao.action';
import { inputStyles, buttonStyles } from './css/commonStyles';

const DevPaymentTab = ({ daoOrganizerAddress, chainId, currentDao }) => {
  const [milestoneIndex, setMilestoneIndex] = useState('');
  const [amount, setAmount] = useState('');
  const [totalMilestones, setTotalMilestones] = useState('');
  const [totalPayment, setTotalPayment] = useState('');
  const [usdtAmount, setUsdtAmount] = useState('');
  const [katPerUsdt, setKatPerUsdt] = useState('');
  const [currentAddress, setCurrentAddress] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isProjectManager, setIsProjectManager] = useState(false);
  const [isContractor, setIsContractor] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [manualDaoId, setManualDaoId] = useState('');
  const [manualTxHash, setManualTxHash] = useState('');
  const [manualMilestoneAmount, setManualMilestoneAmount] = useState('');

  useEffect(() => {
    checkPermissions();
  }, [currentDao]);

  const checkPermissions = async () => {
    try {
      if (!window.ethereum) {
        setError('Please install MetaMask');
        return;
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      const currentAddress = accounts[0];
      setCurrentAddress(currentAddress);

      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, provider);

      // Check admin role
      const adminAddress = await contract.admin();
      setIsAdmin(adminAddress.toLowerCase() === currentAddress.toLowerCase());

      // Check project manager role
      const projectManagerAddress = await contract.projectManager();
      setIsProjectManager(projectManagerAddress.toLowerCase() === currentAddress.toLowerCase());

      // Check contractor role
      const contractorAddress = await contract.contractor();
      setIsContractor(contractorAddress.toLowerCase() === currentAddress.toLowerCase());
    } catch (error) {
      setError('Error checking permissions');
    }
  };

  const handleSetMilestone = async () => {
    if (!isAdmin && !isProjectManager) {
      setError('You do not have permission to set milestone');
      return;
    }
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const milestoneIndexBN = ethers.BigNumber.from(milestoneIndex);
      const amountBN = ethers.utils.parseEther(amount);

      const tx = await contract.setMilestone(milestoneIndexBN, amountBN);
      await tx.wait();
    } catch (error) {
      setError('Error setting milestone');
    }
  };

  const handleSetTotalMilestones = async () => {
    if (!isAdmin && !isProjectManager) {
      setError('You do not have permission to set total milestones');
      return;
    }
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const totalMilestonesBN = ethers.BigNumber.from(totalMilestones);

      const tx = await contract.setTotalMilestones(totalMilestonesBN);
      await tx.wait();
    } catch (error) {
      setError('Error setting total milestones');
    }
  };

  const handleSetTotalPayment = async () => {
    if (!isAdmin && !isProjectManager) {
      setError('You do not have permission to set total payment');
      return;
    }
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const totalPaymentBN = ethers.utils.parseEther(totalPayment);

      const tx = await contract.setTotalPayment(totalPaymentBN);
      await tx.wait();
    } catch (error) {
      setError('Error setting total payment');
    }
  };

  const handleCompleteMilestone = async () => {
    if (!isAdmin && !isProjectManager) {
      setError('You do not have permission to complete milestone');
      return;
    }

    if (!milestoneIndex || !usdtAmount || !katPerUsdt) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const milestoneIndexBN = ethers.BigNumber.from(milestoneIndex);
      const usdtAmountBN = ethers.utils.parseEther(usdtAmount);
      const katPerUsdtBN = ethers.utils.parseEther(katPerUsdt);

      const totalMilestones = await contract.totalMilestones();

      if (milestoneIndexBN.gte(totalMilestones)) {
        setError('Milestone index out of range');
        return;
      }

      const milestone = await contract.milestones(milestoneIndexBN);

      if (milestone.completed) {
        setError('Milestone already completed');
        return;
      }

      if (milestone.amount.lte(0)) {
        setError('Milestone amount not set');
        return;
      }

      if (usdtAmountBN.gt(milestone.amount)) {
        setError('USDT amount exceeds milestone amount');
        return;
      }

      const gasPrice = await provider.getGasPrice();
      const maxGasPrice = ethers.utils.parseUnits('50', 'gwei');
      const finalGasPrice = gasPrice.gt(maxGasPrice) ? maxGasPrice : gasPrice;

      const gasEstimate = await contract.estimateGas.completeMilestone(milestoneIndexBN, usdtAmountBN, katPerUsdtBN);
      const gasLimit = Math.floor(gasEstimate.toNumber() * 1.2);

      const tx = await contract.completeMilestone(milestoneIndexBN, usdtAmountBN, katPerUsdtBN, {
        gasLimit: gasLimit,
        gasPrice: finalGasPrice,
      });

      const receipt = await tx.wait();

      const milestoneAmount = milestone.amount;

      await sendEmailToDevPartner(currentDao._id, receipt.transactionHash, milestoneAmount.toString());

      setLoading(false);
      setSuccess(true);
      setError(null);
    } catch (error) {
      if (error.code === -32603) {
        setError('Transaction failed. Please check your gas limit and try again.');
      } else if (error.message.includes('reverted')) {
        setError(
          'Transaction reverted. Please check if you have the correct permissions and the milestone is in a valid state.',
        );
      } else {
        setError(`Error completing milestone: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailToDevPartner = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await sendEmailToDevPartner(currentDao._id);
      setSuccess('Email sent successfully to dev partner');
    } catch (error) {
      setError('Failed to send email to dev partner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabWrapper daoOrganizerAddress={daoOrganizerAddress} chainId={chainId}>
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          Dev Payment
        </Typography>
        <Typography
          className="saleBetaTitle"
          variant="h6"
          sx={{
            fontSize: '16px',
            fontWeight: 300,
            mb: 3,
            letterSpacing: '0.5px',
            color: '#00EDC3',
          }}
        >
          Milestone Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Current Address: {currentAddress}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Permissions:
            {isAdmin && ' Admin'}
            {isProjectManager && ' Project Manager'}
            {isContractor && ' Contractor'}
            {!isAdmin && !isProjectManager && !isContractor && ' None'}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Set Total Milestones */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Set Total Milestones
              </Typography>
              <TextField
                fullWidth
                label="Total Milestones"
                value={totalMilestones}
                onChange={(e) => setTotalMilestones(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={handleSetTotalMilestones} sx={buttonStyles}>
                Set Total Milestones
              </Button>
            </Box>
          </Grid>

          {/* Set Total Payment */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Set Total Payment
              </Typography>
              <TextField
                fullWidth
                label="Total Payment (ether)"
                value={totalPayment}
                onChange={(e) => setTotalPayment(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter total payment in ether"
              />
              <Button variant="contained" onClick={handleSetTotalPayment} sx={buttonStyles}>
                Set Total Payment
              </Button>
            </Box>
          </Grid>

          {/* Set Milestone */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Set Milestone
              </Typography>
              <TextField
                fullWidth
                label="Milestone Index"
                value={milestoneIndex}
                onChange={(e) => setMilestoneIndex(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Amount (ether)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter amount in ether"
              />
              <Button variant="contained" onClick={handleSetMilestone} sx={buttonStyles}>
                Set Milestone
              </Button>
            </Box>
          </Grid>

          {/* Complete Milestone */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Complete Milestone
              </Typography>
              <TextField
                fullWidth
                label="Milestone Index"
                value={milestoneIndex}
                onChange={(e) => setMilestoneIndex(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="USDT Amount (ether)"
                value={usdtAmount}
                onChange={(e) => setUsdtAmount(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter amount in ether"
              />
              <TextField
                fullWidth
                label="KAT per USDT (ether)"
                value={katPerUsdt}
                onChange={(e) => setKatPerUsdt(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter KAT per USDT in ether"
              />
              <Button
                variant="contained"
                onClick={handleCompleteMilestone}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
                sx={buttonStyles}
              >
                Complete Milestone
              </Button>
              {loading && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: '#00EDC3',
                    fontStyle: 'italic',
                  }}
                >
                  Please do not close this page. Transaction is being processed on the blockchain...
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Send Email to Dev Partner (Manual)
              </Typography>
              <TextField
                fullWidth
                label="Transaction Hash"
                value={manualTxHash}
                onChange={(e) => setManualTxHash(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter Transaction Hash"
                margin="dense"
              />
              <TextField
                fullWidth
                label="Milestone Amount (ether)"
                value={manualMilestoneAmount}
                onChange={(e) => setManualMilestoneAmount(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter Milestone Amount in ether"
                margin="dense"
              />
              <Button
                variant="contained"
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  setSuccess('');
                  try {
                    await sendEmailToDevPartner(currentDao._id, manualTxHash, manualMilestoneAmount);
                    setSuccess('Email sent successfully to dev partner');
                  } catch (error) {
                    setError('Failed to send email to dev partner');
                  } finally {
                    setLoading(false);
                  }
                }}
                sx={buttonStyles}
                disabled={loading}
              >
                Send Email to Dev Partner
              </Button>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              sx={{ ...buttonStyles, mb: 3, background: '#1a1f24', color: '#00EDC3' }}
              onClick={() => window.open('https://app.safe.global/welcome/accounts', '_blank')}
            >
              Go To Safe.global
            </Button>
          </Grid>
        </Grid>
      </Box>
    </TabWrapper>
  );
};

const mapStateToProps = (state) => ({
  currentDao: state.dao?.currentDao || null,
});
const mapDispatchToProps = {
  sendEmailToDevPartner,
};
export default connect(mapStateToProps)(DevPaymentTab);
